/**
 * System Admin -> System Status -> Users tab
 *
 * Sub-tabs:
 *   - Manage Users    user CRUD + role / dashboard / permissions editor
 *   - Online Now      users with active refresh tokens (force-logout action)
 *   - Login Activity  per-user summary: last login, 24h/7d counts, failures, sessions
 *   - User Activity   recent login events (success + failure) with filtering
 *   - Audit Log       paginated audit log with user / action / table filters
 */

import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AdminUser,
  type AuditLogEntry,
  type LoginEvent,
  type LoginActivityRow,
  type OnlineUser,
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/utils';
import {
  UserPlus,
  Edit,
  Trash2,
  LogOut,
  RefreshCw,
  Shield,
  CheckCircle,
  XCircle,
  Search,
  Circle,
  AlertTriangle,
} from 'lucide-react';

const ALL_DASHBOARDS = [
  { id: 'billing_finance', label: 'Finance' },
  { id: 'tax_compliance', label: 'Tax & Compliance' },
  { id: 'customer_operations', label: 'Customer Ops' },
  { id: 'helpdesk_support', label: 'Helpdesk & Support' },
  { id: 'project_management', label: 'Project Mgmt' },
  { id: 'analytics', label: 'Analytics & Reports' },
  { id: 'system_admin', label: 'System Admin' },
] as const;

const ROLES = [
  { id: 'admin', label: 'Admin' },
  { id: 'manager', label: 'Manager' },
  { id: 'user', label: 'User' },
] as const;

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function RoleBadge({ role }: { role: string }) {
  const styles: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-700 border-purple-200',
    manager: 'bg-blue-100 text-blue-700 border-blue-200',
    user: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return <Badge className={styles[role] || styles.user}>{role}</Badge>;
}

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <Badge className="bg-green-100 text-green-700 border-green-200">
      <CheckCircle className="h-3 w-3 mr-1" />Active
    </Badge>
  ) : (
    <Badge variant="outline" className="text-muted-foreground">
      <XCircle className="h-3 w-3 mr-1" />Disabled
    </Badge>
  );
}

function EventTypeBadge({ type }: { type: string }) {
  if (type === 'login_success') {
    return <Badge className="bg-green-100 text-green-700 border-green-200">success</Badge>;
  }
  if (type === 'login_failed') {
    return <Badge variant="destructive">failed</Badge>;
  }
  if (type === 'logout') {
    return <Badge className="bg-blue-100 text-blue-700 border-blue-200">logout</Badge>;
  }
  return <Badge variant="outline">{type}</Badge>;
}

// ---------------------------------------------------------------------------
// Manage Users sub-tab
// ---------------------------------------------------------------------------

interface UserFormState {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'user';
  dashboardAccess: string[];
  isActive: boolean;
}

const EMPTY_FORM: UserFormState = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  dashboardAccess: ['customer_operations'],
  isActive: true,
};

function ManageUsersTab({ active }: { active: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.listUsers({ limit: 100 }),
    enabled: active,
  });

  const users = (data?.data as AdminUser[] | undefined) ?? [];
  const filtered = search
    ? users.filter(
        (u) =>
          u.email.toLowerCase().includes(search.toLowerCase()) ||
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const createMutation = useMutation({
    mutationFn: (input: UserFormState) =>
      api.createUser({
        email: input.email,
        password: input.password,
        firstName: input.firstName,
        lastName: input.lastName,
        role: input.role,
        dashboardAccess: input.dashboardAccess,
        isActive: input.isActive,
      }),
    onSuccess: (res) => {
      if (!res.success) {
        toast({
          title: 'Failed to create user',
          description: res.error?.message,
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'User created' });
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err: Error) =>
      toast({ title: 'Failed to create user', description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: Partial<UserFormState> }) =>
      api.updateUser(input.id, {
        email: input.patch.email,
        firstName: input.patch.firstName,
        lastName: input.patch.lastName,
        role: input.patch.role,
        dashboardAccess: input.patch.dashboardAccess,
        isActive: input.patch.isActive,
      }),
    onSuccess: (res) => {
      if (!res.success) {
        toast({
          title: 'Failed to update user',
          description: res.error?.message,
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'User updated' });
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err: Error) =>
      toast({ title: 'Failed to update user', description: err.message, variant: 'destructive' }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateUser(id),
    onSuccess: () => {
      toast({ title: 'User deactivated' });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['online-users'] });
    },
    onError: (err: Error) =>
      toast({ title: 'Failed to deactivate', description: err.message, variant: 'destructive' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.revokeUserSessions(id),
    onSuccess: (res) => {
      const count =
        (res.data as { sessionsRevoked?: number } | undefined)?.sessionsRevoked ?? 0;
      toast({ title: `Revoked ${count} session${count === 1 ? '' : 's'}` });
      qc.invalidateQueries({ queryKey: ['online-users'] });
    },
    onError: (err: Error) =>
      toast({
        title: 'Failed to revoke sessions',
        description: err.message,
        variant: 'destructive',
      }),
  });

  function openCreate() {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(user: AdminUser) {
    setEditingUser(user);
    setForm({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      dashboardAccess: user.dashboardAccess,
      isActive: user.isActive,
    });
    setDialogOpen(true);
  }

  function submit() {
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, patch: form });
    } else {
      if (!form.password) {
        toast({
          title: 'Password required',
          description: 'New users need an initial password',
          variant: 'destructive',
        });
        return;
      }
      createMutation.mutate(form);
    }
  }

  function toggleDashboard(id: string) {
    setForm((f) => ({
      ...f,
      dashboardAccess: f.dashboardAccess.includes(id)
        ? f.dashboardAccess.filter((d) => d !== id)
        : [...f.dashboardAccess, id],
    }));
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Users ({users.length})</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm w-64"
              />
            </div>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" className="h-8" onClick={openCreate}>
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              Add User
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs">
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Role</th>
                <th className="text-left py-2 pr-3">Dashboards</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2 pr-3">Last Login</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No users
                  </td>
                </tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 font-medium">{u.email}</td>
                  <td className="py-2 pr-3">
                    {u.firstName} {u.lastName}
                  </td>
                  <td className="py-2 pr-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground max-w-xs truncate">
                    {u.dashboardAccess.length === ALL_DASHBOARDS.length
                      ? 'All'
                      : u.dashboardAccess.join(', ') || '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <ActiveBadge isActive={u.isActive} />
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">
                    {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'}
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => openEdit(u)}
                        title="Edit"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => {
                          if (confirm(`Force logout ${u.email}?`)) revokeMutation.mutate(u.id);
                        }}
                        title="Revoke sessions"
                      >
                        <LogOut className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-red-600 hover:text-red-700"
                        onClick={() => {
                          if (
                            u.isActive &&
                            confirm(`Deactivate ${u.email}? They will be logged out everywhere.`)
                          ) {
                            deactivateMutation.mutate(u.id);
                          }
                        }}
                        disabled={!u.isActive}
                        title="Deactivate"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name</Label>
                <Input
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            {!editingUser && (
              <div>
                <Label>Initial Password</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 8 chars, upper + lower + digit"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v as UserFormState['role'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={form.isActive ? 'active' : 'inactive'}
                  onValueChange={(v) => setForm({ ...form, isActive: v === 'active' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Shield className="h-3.5 w-3.5" />
                Dashboard Access
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ALL_DASHBOARDS.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 px-2 py-1.5 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={form.dashboardAccess.includes(d.id)}
                      onChange={() => toggleDashboard(d.id)}
                      className="h-4 w-4"
                    />
                    <span>{d.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Online Now sub-tab
// ---------------------------------------------------------------------------

function OnlineNowTab({ active }: { active: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['online-users'],
    queryFn: () => api.getOnlineUsers(),
    enabled: active,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });

  const rows = (data?.data as OnlineUser[] | undefined) ?? [];

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.revokeUserSessions(id),
    onSuccess: (res) => {
      const count =
        (res.data as { sessionsRevoked?: number } | undefined)?.sessionsRevoked ?? 0;
      toast({ title: `Revoked ${count} session${count === 1 ? '' : 's'}` });
      qc.invalidateQueries({ queryKey: ['online-users'] });
    },
    onError: (err: Error) =>
      toast({ title: 'Failed', description: err.message, variant: 'destructive' }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Circle className="h-3 w-3 fill-green-500 text-green-500 animate-pulse" />
            Online Now ({rows.length})
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-8" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs">
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Role</th>
                <th className="text-left py-2 pr-3">Last Login</th>
                <th className="text-left py-2 pr-3">Most Recent Session</th>
                <th className="text-right py-2 pr-3">Active Sessions</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No users are currently online
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 font-medium">{r.email}</td>
                  <td className="py-2 pr-3">
                    {r.firstName} {r.lastName}
                  </td>
                  <td className="py-2 pr-3">
                    <RoleBadge role={r.role} />
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">
                    {r.lastLoginAt ? formatDateTime(r.lastLoginAt) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">
                    {formatDateTime(r.mostRecentSessionAt)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">{r.activeSessionCount}</td>
                  <td className="py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-red-600 hover:text-red-700"
                      onClick={() => {
                        if (confirm(`Force logout ${r.email}?`)) revokeMutation.mutate(r.id);
                      }}
                    >
                      <LogOut className="h-3.5 w-3.5 mr-1" />
                      Logout
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Login Activity sub-tab
// ---------------------------------------------------------------------------

function LoginActivityTab({ active }: { active: boolean }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['login-activity'],
    queryFn: () => api.getLoginActivity(),
    enabled: active,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const rows = (data?.data as LoginActivityRow[] | undefined) ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Login Activity (per user)</CardTitle>
          <Button variant="ghost" size="sm" className="h-8" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs">
                <th className="text-left py-2 pr-3">User</th>
                <th className="text-left py-2 pr-3">Role</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2 pr-3">Last Login</th>
                <th className="text-right py-2 pr-3">Logins 24h</th>
                <th className="text-right py-2 pr-3">Logins 7d</th>
                <th className="text-right py-2 pr-3">Failures 24h</th>
                <th className="text-right py-2">Active Sessions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3">
                    <div className="font-medium">
                      {r.firstName} {r.lastName}
                    </div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <RoleBadge role={r.role} />
                  </td>
                  <td className="py-2 pr-3">
                    <ActiveBadge isActive={r.isActive} />
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">
                    {r.lastLoginAt ? formatDateTime(r.lastLoginAt) : 'Never'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">{r.logins24h}</td>
                  <td className="py-2 pr-3 text-right font-mono">{r.logins7d}</td>
                  <td
                    className={`py-2 pr-3 text-right font-mono ${
                      r.failures24h > 0 ? 'text-red-600 font-bold' : ''
                    }`}
                  >
                    {r.failures24h > 0 && (
                      <AlertTriangle className="inline h-3 w-3 mr-1" />
                    )}
                    {r.failures24h}
                  </td>
                  <td className="py-2 text-right font-mono">{r.activeSessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// User Activity (login events) sub-tab
// ---------------------------------------------------------------------------

function UserActivityTab({ active }: { active: boolean }) {
  const [eventType, setEventType] = useState<string>('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['login-events', eventType],
    queryFn: () =>
      api.getLoginEvents({
        limit: 100,
        eventType: (eventType || undefined) as
          | 'login_success'
          | 'login_failed'
          | 'logout'
          | 'token_refresh'
          | undefined,
      }),
    enabled: active,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const rows = (data?.data as LoginEvent[] | undefined) ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">User Activity (Login Events)</CardTitle>
          <div className="flex gap-2">
            {[
              { v: '', l: 'All' },
              { v: 'login_success', l: 'Success' },
              { v: 'login_failed', l: 'Failed' },
              { v: 'logout', l: 'Logout' },
            ].map((f) => (
              <Button
                key={f.v || 'all'}
                variant={eventType === f.v ? 'secondary' : 'outline'}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setEventType(f.v)}
              >
                {f.l}
              </Button>
            ))}
            <Button variant="ghost" size="sm" className="h-8" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-left py-2 pr-3">Event</th>
                <th className="text-left py-2 pr-3">User / Email</th>
                <th className="text-left py-2 pr-3">Reason</th>
                <th className="text-left py-2 pr-3">IP</th>
                <th className="text-left py-2">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No events
                  </td>
                </tr>
              )}
              {rows.map((e) => (
                <tr
                  key={e.id}
                  className={`border-b last:border-0 hover:bg-muted/30 ${
                    e.eventType === 'login_failed' ? 'bg-red-50' : ''
                  }`}
                >
                  <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                    {formatDateTime(e.createdAt)}
                  </td>
                  <td className="py-1.5 pr-3">
                    <EventTypeBadge type={e.eventType} />
                  </td>
                  <td className="py-1.5 pr-3">
                    {e.userName ? (
                      <>
                        <div className="font-medium">{e.userName}</div>
                        <div className="text-muted-foreground">{e.email}</div>
                      </>
                    ) : (
                      <span className="font-mono">{e.email}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-red-600">{e.failureReason || '—'}</td>
                  <td className="py-1.5 pr-3 font-mono text-muted-foreground">
                    {e.ipAddress || '—'}
                  </td>
                  <td
                    className="py-1.5 text-muted-foreground max-w-[300px] truncate"
                    title={e.userAgent || ''}
                  >
                    {e.userAgent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Audit Log sub-tab
// ---------------------------------------------------------------------------

function AuditLogTab({ active }: { active: boolean }) {
  const [action, setAction] = useState('');
  const [tableName, setTableName] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-log', action, tableName],
    queryFn: () =>
      api.getAuditLog({
        limit: 100,
        action: action || undefined,
        tableName: tableName || undefined,
      }),
    enabled: active,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const rows = (data?.data as AuditLogEntry[] | undefined) ?? [];

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function formatJson(val: string | null): string {
    if (!val) return '—';
    try {
      return JSON.stringify(JSON.parse(val), null, 2);
    } catch {
      return val;
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Audit Log</CardTitle>
          <div className="flex gap-2">
            <Input
              placeholder="Filter action..."
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="h-8 text-sm w-32"
            />
            <Input
              placeholder="Filter table..."
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              className="h-8 text-sm w-36"
            />
            <Button variant="ghost" size="sm" className="h-8" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="w-6" />
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-left py-2 pr-3">Action</th>
                <th className="text-left py-2 pr-3">Table</th>
                <th className="text-left py-2 pr-3">User</th>
                <th className="text-left py-2 pr-3">Record</th>
                <th className="text-left py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No audit entries
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isOpen = expanded.has(r.id);
                const hasDetails = r.oldValues || r.newValues;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={`border-b last:border-0 hover:bg-muted/30 ${
                        hasDetails ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => hasDetails && toggle(r.id)}
                    >
                      <td className="py-1.5 text-center text-muted-foreground">
                        {hasDetails ? (isOpen ? '▾' : '▸') : ''}
                      </td>
                      <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                        {formatDateTime(r.createdAt)}
                      </td>
                      <td className="py-1.5 pr-3">
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {r.action}
                        </Badge>
                      </td>
                      <td className="py-1.5 pr-3 font-mono">{r.tableName}</td>
                      <td className="py-1.5 pr-3">
                        {r.userEmail || (
                          <span className="text-muted-foreground italic">system</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-muted-foreground truncate max-w-[180px]">
                        {r.recordId || '—'}
                      </td>
                      <td className="py-1.5 font-mono text-muted-foreground">
                        {r.ipAddress || '—'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-muted/20">
                        <td colSpan={7} className="py-2 px-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium mb-1">Before</p>
                              <pre className="text-[10px] bg-background border rounded p-2 overflow-x-auto max-h-48">
                                {formatJson(r.oldValues)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-xs font-medium mb-1">After</p>
                              <pre className="text-[10px] bg-background border rounded p-2 overflow-x-auto max-h-48">
                                {formatJson(r.newValues)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main UsersTab
// ---------------------------------------------------------------------------

export function UsersTab() {
  const [sub, setSub] = useState('manage');

  return (
    <Tabs value={sub} onValueChange={setSub}>
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="manage">Manage Users</TabsTrigger>
        <TabsTrigger value="online">Online Now</TabsTrigger>
        <TabsTrigger value="activity">Login Activity</TabsTrigger>
        <TabsTrigger value="events">User Activity</TabsTrigger>
        <TabsTrigger value="audit">Audit Log</TabsTrigger>
      </TabsList>

      <TabsContent value="manage" className="mt-4">
        <ManageUsersTab active={sub === 'manage'} />
      </TabsContent>
      <TabsContent value="online" className="mt-4">
        <OnlineNowTab active={sub === 'online'} />
      </TabsContent>
      <TabsContent value="activity" className="mt-4">
        <LoginActivityTab active={sub === 'activity'} />
      </TabsContent>
      <TabsContent value="events" className="mt-4">
        <UserActivityTab active={sub === 'events'} />
      </TabsContent>
      <TabsContent value="audit" className="mt-4">
        <AuditLogTab active={sub === 'audit'} />
      </TabsContent>
    </Tabs>
  );
}
